import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShieldAlert, ChevronRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { getBundle } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { LEGAL_NOTICE } from "@/config";

const gbp = (n) => `£${n.toFixed(2)}`;

export default function BundleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setBundle(null);
    getBundle(id).then(setBundle).catch(() => navigate("/bundles"));
  }, [id, navigate]);

  if (!bundle) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <Skeleton className="aspect-[16/7] w-full rounded-2xl bg-slate-100" />
      </div>
    );
  }

  const savings = bundle.value - bundle.price;
  const add = () => {
    if (!addItem(bundle, "bundle", 1)) return;
    toast.success(`${bundle.name} added to cart`);
    setOpen(true);
  };

  return (
    <div className="pt-8 pb-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center gap-1.5 text-sm text-slate-500">
          <Link to="/" className="hover:text-slate-700">Home</Link><ChevronRight className="h-3.5 w-3.5" />
          <Link to="/bundles" className="hover:text-slate-700">Bundles</Link><ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-700">{bundle.name}</span>
        </nav>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6 overflow-hidden rounded-2xl bg-slate-100 metallic-border">
          <div className="relative aspect-[16/7]">
            <img src={bundle.image} alt={bundle.name} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E]/90 via-[#0A0B0E]/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <span className="font-mono2 text-xs uppercase tracking-widest text-slate-200">Bundle</span>
              <h1 className="mt-1 font-display text-3xl font-bold text-white sm:text-5xl">{bundle.name}</h1>
              <p className="mt-2 max-w-xl text-slate-200">{bundle.tagline}</p>
            </div>
          </div>
        </motion.div>

        <div className="mt-10 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold text-slate-900">About this bundle</h2>
            <p className="mt-4 leading-relaxed text-slate-600">{bundle.description}</p>

            <h3 className="mt-8 font-display text-lg font-semibold text-slate-900">What's included</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {(bundle.items || []).map((item) => (
                <Link to={`/product/${item.id}`} key={item.id} className="group flex gap-3 rounded-xl bg-white metallic-border p-3 card-hover">
                  <img src={item.image} alt={item.name} className="h-16 w-16 rounded-lg object-cover" />
                  <div>
                    <p className="text-sm font-medium text-slate-900 group-hover:text-slate-900">{item.name}</p>
                    <p className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{item.category}</p>
                    <p className="mt-1 text-sm text-slate-600">{gbp(item.price)}</p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 flex gap-3 rounded-xl border border-amber-500/40 bg-amber-50 p-4">
              <ShieldAlert className="h-5 w-5 shrink-0 text-amber-500" />
              <p className="text-xs leading-relaxed text-amber-900"><span className="font-semibold">Compliance Notice: </span>{LEGAL_NOTICE}</p>
            </div>
          </div>

          <aside>
            <div className="sticky top-24 rounded-2xl glass-strong metallic-border p-6">
              <h4 className="font-display text-base font-semibold text-slate-900">Value comparison</h4>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600"><span>Items purchased separately</span><span className="text-slate-500 line-through">{gbp(bundle.value)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Bundle price</span><span className="font-semibold metallic-text">{gbp(bundle.price)}</span></div>
                {savings > 0 && <div className="flex justify-between text-emerald-600"><span>You save</span><span>{gbp(savings)}</span></div>}
              </div>
              <ul className="mt-5 space-y-2 border-t border-slate-200 pt-5">
                {bundle.includes.map((i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-emerald-500" /> {i}</li>
                ))}
              </ul>
              <Button data-testid="bundle-detail-add" onClick={add} disabled={bundle.sold_out} className="mt-6 w-full btn-metallic h-11 font-semibold">{bundle.sold_out ? "Sold out" : "Add Bundle to Cart"}</Button>
              <Link to="/bundles" className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> All bundles</Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
